import { type AllSportsItems, type AllSportsLink, variant } from '@cx-web/domain-types';

import { useDesktopHeaderTracking } from '../../context/DesktopHeaderTrackingContext';
import { mergePersistentParams } from '../../utils/merge-persistent-params';

import styles from './SecondaryAllSports.module.css';

interface SecondaryAllSportsProps {
  items: AllSportsItems;
  name: string;
  serverUrl?: string;
}

function groupByFirstLetter(allLinks: AllSportsLink[]): [string, AllSportsLink[]][] {
  const map: Record<string, AllSportsLink[]> = {};
  for (const sport of allLinks) {
    const letter = sport.name[0]?.toUpperCase() ?? '#';
    if (!map[letter]) map[letter] = [];
    map[letter].push(sport);
  }
  return Object.entries(map)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(
      ([letter, sports]) =>
        [letter, [...sports].sort((a, b) => a.name.localeCompare(b.name))] as [string, AllSportsLink[]],
    );
}

export const SecondaryAllSports = ({ items, name, serverUrl = '' }: SecondaryAllSportsProps) => {
  const { allLinks } = items;
  const grouped = groupByFirstLetter(allLinks);
  const track = useDesktopHeaderTracking();

  return (
    <div className={styles.container} data-testid="secondary-all-sports">
      <div className={styles.inner}>
        <h2 className={styles.header} data-testid="secondary-all-sports-heading">
          {name}
        </h2>

        <div className={styles.allLinks}>
          {grouped.map(([letter, sports]) => (
            <div className={styles.letterGroup} key={letter}>
              <span className={styles.letterMarker} data-testid="secondary-all-sports-letter-marker">
                {letter}
              </span>
              <div className={styles.sportsInGroup}>
                {sports.map(sport => (
                  <div className={styles.sportEntry} key={sport.name}>
                    <a
                      className={styles.sportLink}
                      data-testid="secondary-all-sports-sport-link"
                      href={mergePersistentParams(sport.link, serverUrl)}
                      onClick={() => track(variant.secondary, sport.name)}
                    >
                      {sport.name}
                    </a>
                    {sport.subLinks.length > 0 && (
                      <ul className={styles.subLinkList} data-testid={`secondary-all-sports-sublinks-${sport.name}`}>
                        {sport.subLinks.map((sub, idx) => (
                          <li key={sub.name ?? sub.link ?? String(idx)}>
                            <a
                              className={styles.subLink}
                              data-testid="secondary-all-sports-sub-link"
                              href={mergePersistentParams(sub.link ?? '', serverUrl)}
                              onClick={() => track(variant.secondary, sub.name)}
                            >
                              {sub.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
